import { Plus, X } from 'lucide-react';
import { AGENTS, DEPARTMENTS } from '../../utils/agents';
import { useApprovalStore } from '../../stores/approvalStore';

const PRIORITY_OPTIONS = ['urgent', 'high', 'medium', 'low'] as const;
const STATUS_OPTIONS = [
  { key: 'pending', label: 'Pending' },
  { key: 'revision_requested', label: 'Revision Requested' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
] as const;

const C_SUITE_AGENTS = AGENTS.filter((a) => a.isCSuite);

interface ApprovalFiltersProps {
  onNewApproval: () => void;
}

export default function ApprovalFilters({ onNewApproval }: ApprovalFiltersProps) {
  const filters = useApprovalStore((s) => s.filters);
  const setFilters = useApprovalStore((s) => s.setFilters);

  const hasFilters = filters.approver || filters.department || filters.status || filters.priority;

  function clearFilters() {
    setFilters({ approver: undefined, department: undefined, status: undefined, priority: undefined });
  }

  return (
    <div className="bg-bg-secondary rounded-lg p-3 flex flex-row gap-3 items-center flex-wrap">
      <select
        value={filters.approver || ''}
        onChange={(e) => setFilters({ approver: e.target.value || undefined })}
        className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-1.5 text-sm"
      >
        <option value="">All Approvers</option>
        <option value="owner">Owners (Aaron & Taylor)</option>
        {C_SUITE_AGENTS.map((a) => (
          <option key={a.slug} value={a.slug}>
            {a.name} -- {a.shortName}
          </option>
        ))}
      </select>

      <select
        value={filters.department || ''}
        onChange={(e) => setFilters({ department: e.target.value || undefined })}
        className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-1.5 text-sm"
      >
        <option value="">All Departments</option>
        {DEPARTMENTS.map((d) => (
          <option key={d.id} value={d.id}>
            {d.name}
          </option>
        ))}
      </select>

      <select
        value={filters.status || ''}
        onChange={(e) => setFilters({ status: e.target.value || undefined })}
        className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-1.5 text-sm"
      >
        <option value="">All Statuses</option>
        {STATUS_OPTIONS.map((s) => (
          <option key={s.key} value={s.key}>
            {s.label}
          </option>
        ))}
      </select>

      <select
        value={filters.priority || ''}
        onChange={(e) => setFilters({ priority: e.target.value || undefined })}
        className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-1.5 text-sm"
      >
        <option value="">All Priorities</option>
        {PRIORITY_OPTIONS.map((p) => (
          <option key={p} value={p}>
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </option>
        ))}
      </select>

      {hasFilters ? (
        <button
          onClick={clearFilters}
          className="text-text-muted hover:text-text-secondary text-sm flex items-center gap-1 transition-colors"
        >
          <X className="h-3.5 w-3.5" />
          Clear
        </button>
      ) : null}

      <div className="flex-1" />

      <button
        onClick={onNewApproval}
        className="bg-accent-purple text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-accent-purple/80 transition-colors flex items-center gap-2"
      >
        <Plus className="h-4 w-4" />
        New Approval
      </button>
    </div>
  );
}
